/* The state store, WITHOUT a state-management library (client-framework.md
   CF5). A tiny external store — a mutable ClientState, a listener set, and
   `commit()` to publish a new snapshot — read by React through
   useSyncExternalStore in store.tsx. The action methods are ported from
   src/client/app.ts: the vanilla client mutated its single `state` object and
   called render(doc); here an action mutates `this.state` and calls commit(),
   which is React's cue to repaint. Every REST call is byte-identical to the
   vanilla source (same endpoint, method, and body) — the API contract is
   frozen. */
import type { Project, RecordState } from "../../../shared/projects";
import type { Role } from "../../../shared/roles";
import type { PdpCriterion, PdpDeliverable, PdpGate } from "../../../shared/pdp";
import type { Product, Vehicle } from "../../../shared/products";
import { currentEvidence, type ProductPdp } from "../../../shared/product-pdp";
import type { FeedReading, FeedRef, FeedSourceInfo } from "../../../shared/feeds";
import type { TimeEntryLink } from "../../../shared/time-entry";
import type { OrganizerRole } from "../../../shared/organizer-roles";
import type { Team } from "../../../shared/teams";
import type { Directory, DirectorySection } from "../../../shared/directories";
import { validateDirectoryInput } from "../../../shared/directories";
import { fmtMonth } from "../../../shared/months";
import { CATALOG_ROLE_IDS } from "../../../shared/roles";
import { enabled, FEATURES, type FeatureId } from "../../../shared/features";
import { feedKey, initialState, landingView, readStartView, type ClientState, type FeedRowReading, type Me, type UserWithGrants } from "./state";
import {
  createContext,
  createElement,
  useContext,
  useRef,
  useSyncExternalStore,
  type JSX,
  type ReactNode,
} from "react";

/* ---- fetch helpers, verbatim from app.ts ---- */

export async function loadJson<T>(path: string): Promise<T> {
  const res = await fetch(path);
  if (!res.ok) throw new Error(`GET ${path} → ${res.status}`);
  return (await res.json()) as T;
}

async function failureReason(
  res: { status?: number; json(): Promise<unknown> },
  fallback: string,
): Promise<string> {
  try {
    const { error } = (await res.json()) as { error?: string };
    if (error) return error;
  } catch {
    /* no readable body — fall through to the status */
  }
  return unauthoredReason(res.status, fallback);
}

function unauthoredReason(status: number | undefined, fallback: string): string {
  const action = fallback.split(" — ")[0];
  if (status === 401 || status === 403)
    return `${action} — the request was refused before it reached the app; reload the page to sign in again. (HTTP ${status})`;
  return status === undefined ? fallback : `${fallback} (HTTP ${status})`;
}

type PendingFlag =
  | "loadPending"
  | "teamsPending"
  | "myPending"
  | "dirsPending"
  | "usersPending";

/* Views that are a reader's UNSAVED WORK (app.ts FORM_VIEWS). A background
   group answering must not repaint over half-typed form input. */
const FORM_VIEWS = ["project-form", "allocation-form", "item-form", "mapping-form"];

export class Store {
  state: ClientState = initialState();
  private listeners = new Set<() => void>();
  /* init() can run again on the same store (tests, hot reload) — one delegated
     handler, one load, guarded like the vanilla conceptMoreWired WeakSet */
  private loaded = false;
  /* React's PUBLISHED snapshot: a fresh-identity clone made on every commit(),
     what useSyncExternalStore hands components. `this.state` (the live working
     copy) is NEVER reassigned — that stability is the fix for the load-race
     class of bug: a `this.state.X = await Y` write can no longer be orphaned by
     a concurrent commit swapping the object out from under the pending write. */
  private snapshot: ClientState;

  constructor() {
    /* Arm the load groups' pending flags at construction — BEFORE React reads
       the first snapshot — so the very first paint is the neutral workspace
       skeleton, never a flash of empty arrays asserting "not recorded" (MA5
       state 0). The vanilla shell armed these at the top of init(), before its
       first render(doc); the store is that entry point here. load() re-arms
       them idempotently for a re-init. */
    this.state.mePending = true;
    this.state.loadPending = true;
    this.state.teamsPending = true;
    this.state.myPending = true;
    this.state.dirsPending = true;
    /* publish the armed initial state as the first snapshot */
    this.snapshot = { ...this.state };
  }

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): ClientState => this.snapshot;

  /* Publish a new snapshot — React's cue to repaint, the peer of the vanilla
     render(doc). this.state is mutated in place and NEVER reassigned; commit
     clones it into a fresh-identity snapshot React can diff. Keeping this.state
     stable is what makes every `this.state.X = await Y` safe (see the snapshot
     field note): the pending assignment always targets the live object, so a
     concurrent commit can no longer orphan it. */
  commit(): void {
    this.snapshot = { ...this.state };
    for (const fn of this.listeners) fn();
  }

  /* generic escape hatch for simple field writes from components (e.g. a
     form's controlled inputs, the picker search box) — set then commit */
  update(partial: Partial<ClientState>): void {
    Object.assign(this.state, partial);
    this.commit();
  }

  /* Does this surface show the feature? (feature-surface.md FS1/FS5.) */
  on = (id?: FeatureId): boolean => id === undefined || enabled(id, this.state.me.surface);

  /* Which allocations read this reader is entitled to (RA4). */
  allocationsPath = (): string =>
    ["admin", "record-keeper", "dev-manager"].some((r) => this.state.me.roles.includes(r))
      ? "/api/allocations/planning"
      : "/api/allocations";

  /* ---- the load sequence, ported from init() (app.ts ~5368) ---- */

  async load(): Promise<void> {
    if (this.loaded) return;
    this.loaded = true;
    /* Arm every group's pending flag BEFORE the shell's first paint (MA5
       state 0): a first paint against empty arrays would assert a red "not
       recorded" gap for a question nobody has asked yet. */
    const s = this.state;
    s.mePending = true;
    s.loadPending = true;
    s.teamsPending = true;
    s.myPending = true;
    s.dirsPending = true;
    this.commit();
    /* /api/me first, serialized (FS4): the surface decides the fetch list, so
       it must be known before the groups launch. Fail closed to release. */
    this.state.me = await loadJson<Me>("/api/me").catch(
      (): Me => ({ user: null, roles: [], manages: [], surface: "release" }),
    );
    this.state.mePending = false;
    /* the landing derives from the registry the moment the surface is known
       (MA2) — but only the INITIAL view: a reader who already navigated during
       the serialized hop keeps the page they chose. */
    if (!this.state.navigated)
      this.state.view = landingView(readStartView(), this.on);
    this.commit();
    /* the remaining fault-isolated groups mirror the server's fault domains
       (OH6): teams source, store, directories, and the caller's My Work rows.
       Each failure costs exactly its own feature; they load concurrently. */
    await Promise.all([
      loadJson<Team[]>("/api/teams")
        .then((teams) => {
          this.state.teams = teams;
          this.state.teamsError = false;
        })
        .catch(() => {
          this.state.teams = [];
          this.state.teamsError = true;
        })
        .finally(() => this.settled("teamsPending")),
      (async () => {
        try {
          /* Capture into locals, then assign in ONE synchronous step. A bare
             `this.state.X = await Y` binds the LHS `this.state` reference BEFORE
             the await resolves; a CONCURRENT group's commit() swaps this.state
             to a fresh clone during that await, so the write would land on the
             orphaned old object and be lost (the teams/dirs groups escape this
             by writing inside a .then, with no await between reading this.state
             and assigning). The single Object.assign below reads this.state once,
             after every await has settled. */
          const projects = await loadJson<Project[]>("/api/projects");
          const roles = await loadJson<Role[]>("/api/roles");
          const timeEntryLinks = await loadJson<TimeEntryLink[]>("/api/time-entry-links");
          Object.assign(this.state, { projects, roles, timeEntryLinks });
        } catch {
          this.state.loadError = true;
        } finally {
          this.settled("loadPending");
        }
      })(),
      loadJson<Directory[]>("/api/directories")
        .then((dirs) => {
          this.state.directories = dirs;
          this.state.dirsError = false;
        })
        .catch(() => {
          this.state.directories = [];
          this.state.dirsError = true;
        })
        .finally(() => this.settled("dirsPending")),
    ]);
  }

  /* One load group has answered (OH6): clear the flag and paint HERE, so a
     section that has its answer shows it immediately. A settle mid-edit of a
     form view clears the flag but does NOT repaint — React holds the form's
     controlled inputs, and the form's own save/cancel re-renders. */
  private settled(flag: PendingFlag): void {
    this.state[flag] = false;
    if (FORM_VIEWS.includes(this.state.view)) {
      /* still publish for non-form consumers via a shallow-clone-free notify?
         No — a form view means the reader is typing; match the vanilla skip. */
      return;
    }
    this.commit();
  }

  /* ---- navigation, ported verbatim (app.ts) ---- */

  private closeExplore(): void {
    this.state.openMenu = null;
    this.state.pickerOpen = false;
    /* File operates on records, so it returns you to them (N17) */
    this.state.area = null;
  }

  /* the table's filters are per-section views — a folder id from one section
     means nothing in another, so section hops start the table fresh (NR8) */
  private resetTable(): void {
    this.state.tableState = "all";
    this.state.tableDir = "all";
    this.state.tableQuery = "";
  }

  /* the rail's one selection (NR8, the picker's DIR6 applied to the table):
     select the filter, keep the picker's folder-creation parent in sync, and
     toggle the fold so nested folders open under their parent */
  selectTableDir(id: string): void {
    this.state.tableDir = id;
    this.state.selectedDirId = id === "all" || id === "" ? null : id;
    if (id !== "all")
      this.state.expandedDirs = this.state.expandedDirs.includes(id)
        ? this.state.expandedDirs.filter((x) => x !== id)
        : [...this.state.expandedDirs, id];
    this.commit();
  }

  selectNav(view: ClientState["view"]): void {
    this.state.view = view;
    this.state.navigated = true;
    this.state.listError = null;
    this.resetTable();
    this.closeExplore();
    /* Teams has no record list to return to — its records ARE the document
       (D7), so returning to the section returns to the open team's Roster */
    if (view === "teams" && this.state.openTeamId) this.state.area = "Roster";
    /* a product record, like a team, is its own document: returning to the
       section returns to the open product's Phase area (products-pdp.md PP15) */
    if (view === "products" && this.state.openProductId) this.state.area = "Phase";
    if (view === "permissions") void this.openPermissions();
    else if (view === "roles") void this.openRoles();
    else if (view === "pdp") {
      /* the section opens on its first tab (pdp-catalog.md PC1) */
      this.state.area = "Deliverables";
      void this.openPdp();
    } else if (view === "products") void this.openProducts();
    else this.commit();
  }

  /* ---- the Products section (products-pdp.md PP15) ---- */

  openProduct(id: string): void {
    const s = this.state;
    s.openProductId = id;
    s.navigated = true;
    s.view = "products";
    /* the record opens on its Phase area, its Deliverables filters fresh */
    s.area = "Phase";
    s.phaseFormOpen = false;
    s.productFormOpen = null;
    this.resetDeliverables();
    s.openMenu = null;
    s.pickerOpen = false;
    s.listError = null;
    this.commit();
    const pdp = s.productPdp.find((p) => p.productId === id);
    if (pdp) void this.loadFeedReadings(pdp);
  }

  /* The open record's LOGGED feed readings (kpi-feed-seam.md KF12 — a page
     view is not a measurement): one free GET /api/feeds/reading per standing
     feed ref, best-effort — a failed read leaves the ref unknown, so the row
     stays `not read yet` with its Read button and the section never errors.
     `onlyNew` (a save landing) skips refs the page already holds, so a
     Retry's stamp survives the save. A ref already in flight is left alone. */
  async loadFeedReadings(pdp: ProductPdp, onlyNew = false): Promise<void> {
    const refs = new Map<string, FeedRef>();
    for (const history of Object.values(pdp.evidence)) {
      const cur = currentEvidence(history);
      if (cur?.kind === "feed") refs.set(feedKey(cur.ref), cur.ref);
    }
    await Promise.all(
      [...refs].map(async ([key, ref]) => {
        const held = this.state.feedReadings[key];
        if (held?.inFlight || (onlyNew && held)) return;
        try {
          const reading = await loadJson<FeedReading>(
            `/api/feeds/reading?sourceId=${encodeURIComponent(ref.sourceId)}&measure=${encodeURIComponent(ref.measure)}`,
          );
          /* the record closed or changed while the read was out, or the row's
             own Read answered meanwhile — the newer value stands */
          if (this.state.openProductId !== pdp.productId || this.state.feedReadings[key] !== held) return;
          this.putFeedReading(ref, { reading, inFlight: false, retriedAt: null, httpError: null });
        } catch {
          /* best-effort — the row renders the honest `not read yet` */
        }
      }),
    );
  }

  /* after a gate review the seam just read every feed slot for the
     certificate (KF15), so the rows take the fresh log rows — held or not */
  refreshFeedReadings(): void {
    const pdp = this.state.productPdp.find((p) => p.productId === this.state.openProductId);
    if (pdp) void this.loadFeedReadings(pdp);
  }

  /* one ref's reading and page-session facts, replaced whole (readFeed's
     patches spread the held value first) */
  putFeedReading(ref: FeedRef, value: FeedRowReading): void {
    this.state.feedReadings = { ...this.state.feedReadings, [feedKey(ref)]: value };
    this.commit();
  }

  closeProduct(): void {
    this.state.openProductId = null;
    this.state.area = null;
    this.state.phaseFormOpen = false;
    this.state.productFormOpen = null;
    this.resetDeliverables();
    this.state.openMenu = null;
    /* a withdraw failure shown on the record does not follow the reader out */
    this.state.listError = null;
    this.commit();
  }

  private resetDeliverables(): void {
    Object.assign(this.state, {
      /* the readings are the OPEN product's: a Retry stamp or an HTTP failure
         on one record must not show on another record sharing the ref */
      feedReadings: {},
      deliverablesPhase: "all",
      deliverablesState: "all",
      deliverablesSearch: "",
      evidenceDraft: null,
      reviewDraft: false,
      reviewGateId: null,
      openCertificateId: null,
    });
  }

  /* a read that was superseded by a later openProducts() must not overwrite
     what landed meanwhile; a SAVE that answers during a read is kept aside
     and re-applied over the read's answer — the read still lands (the list
     may have grown), the save is newer than what the read asked for */
  private productsLoad = 0;
  private savedDuringLoad = new Map<string, ProductPdp>();

  /* The section's reads, one fault domain: the product list (intake's
     vocabulary), every PDP record, and the catalog with the Roles it names —
     the table and the record derive everything from these. */
  async openProducts(): Promise<void> {
    const token = ++this.productsLoad;
    this.state.productsPending = true;
    this.state.productsError = false;
    this.state.productsSearch = "";
    this.state.productsShowRetired = false;
    /* a half-typed New product… or Rename… does not survive a section hop (PM4) */
    this.state.productFormOpen = null;
    this.commit();
    try {
      const [products, productPdp, feedSources, vehicles] = await Promise.all([
        loadJson<Product[]>("/api/intake/products"),
        loadJson<ProductPdp[]>("/api/products/pdp"),
        /* best-effort: the feed routes live on their own feature (pdp-feeds,
           the family's kill switch), so a 404 here is "no sources", never
           the section's error */
        loadJson<FeedSourceInfo[]>("/api/feeds/sources").catch((): FeedSourceInfo[] => []),
        /* a required column: a failed read is the section's error, never an
           empty list shown as "none recorded" (Copilot on #147) */
        loadJson<Vehicle[]>("/api/intake/vehicles"),
        this.reloadPdp(),
      ]);
      if (token !== this.productsLoad) return;
      if (this.state.pdpError) throw new Error("catalog");
      const saved = this.savedDuringLoad;
      const merged = [...productPdp.filter((p) => !saved.has(p.productId)), ...saved.values()];
      Object.assign(this.state, { products, vehicles, productPdp: merged, feedSources, productsError: false });
      const open = merged.find((p) => p.productId === this.state.openProductId);
      if (open) void this.loadFeedReadings(open);
    } catch {
      if (token !== this.productsLoad) return;
      this.state.productsError = true;
    }
    this.savedDuringLoad = new Map();
    this.state.productsPending = false;
    this.commit();
  }

  /* a saved declaration replaces the product's record in place (the PUT
     answers the whole ProductPdp) — no second read */
  putProductPdp(pdp: ProductPdp): void {
    if (this.state.productsPending) this.savedDuringLoad.set(pdp.productId, pdp);
    const rest = this.state.productPdp.filter((p) => p.productId !== pdp.productId);
    this.state.productPdp = [...rest, pdp];
    /* a stale withdraw failure does not outlive a newer successful write */
    this.state.listError = null;
    this.commit();
    /* a feed pointer just recorded gets its logged reading (KF12: no read
       happens on save — the log answers, or `not read yet`) */
    if (pdp.productId === this.state.openProductId) void this.loadFeedReadings(pdp, true);
  }

  openProject(id: string): void {
    const s = this.state;
    s.openProjectId = id;
    s.roleDraft = null;
    s.demandRole = null;
    s.roleSearch = "";
    s.trendRole = null;
    s.trendFrom = null;
    s.trendTo = null;
    s.trendRangeError = null;
    s.rosterFrom = null;
    s.rosterTo = null;
    s.rosterAsTable = false;
    s.rosterError = null;
    s.charterError = null;
    /* a half-finished charter edit never follows the reader to another record (review, Sep 16 2026) */
    s.charterEditing = false;
    s.navigated = true;
    s.view = "projects";
    s.editingId = null;
    s.area = "Resourcing";
    s.openMenu = null;
    s.pickerOpen = false;
    s.listError = null;
    this.commit();
  }

  closeProject(): void {
    this.state.openProjectId = null;
    this.state.area = null;
    this.state.openMenu = null;
    this.commit();
  }

  openTeam(id: string): void {
    const s = this.state;
    s.openTeamId = id;
    s.navigated = true;
    s.view = "teams";
    s.area = "Roster";
    s.openMenu = null;
    s.pickerOpen = false;
    s.listError = null;
    this.commit();
  }

  closeTeam(): void {
    this.state.openTeamId = null;
    this.state.area = null;
    this.state.openMenu = null;
    this.commit();
  }

  openArea(label: string): void {
    this.state.area = label;
    /* a Record evidence drawer, the review sheet and an open certificate do
       not follow the reader to another area */
    this.state.evidenceDraft = null;
    this.state.reviewDraft = false;
    this.state.reviewGateId = null;
    this.state.openCertificateId = null;
    this.state.openMenu = null;
    this.state.listError = null;
    this.state.rosterError = null;
    this.state.charterError = null;
    this.state.charterEditing = false;
    this.commit();
  }

  /* ---- the report form (issue-tracker.md IT9) ---- */

  openReport(): void {
    this.state.reportOpen = true;
    this.commit();
  }

  closeReport(): void {
    this.state.reportOpen = false;
    this.commit();
  }

  /* ---- drawer / menu bar / picker chrome ---- */

  toggleMenu(force?: boolean): void {
    this.state.menuOpen = typeof force === "boolean" ? force : !this.state.menuOpen;
    this.commit();
  }

  toggleMenuBar(label: string): void {
    this.state.openMenu = this.state.openMenu === label ? null : label;
    this.commit();
  }

  openPicker(): void {
    this.state.pickerOpen = true;
    this.state.pickerSearch = "";
    /* the folder you are standing in starts unfolded (legacy view tree) */
    this.state.openMenu = null;
    this.commit();
  }

  closePicker(): void {
    this.state.pickerOpen = false;
    this.state.pickerSearch = "";
    this.commit();
  }

  setPickerSearch(value: string): void {
    this.state.pickerSearch = value;
    this.commit();
  }

  /* legacy view-tree folder toggle (Teams / Permissions single-view sections):
     select the view AND fold/unfold — while a query is live every surviving
     folder is already unfolded, so there is no fold to toggle (N31/N32) */
  toggleFolder(label: string, select: () => void, hasFiles: boolean): void {
    select();
    this.state.pickerOpen = true;
    const searching = this.state.pickerSearch.trim() !== "";
    if (hasFiles && !searching)
      this.state.expandedFolders = this.state.expandedFolders.includes(label)
        ? this.state.expandedFolders.filter((l) => l !== label)
        : [...this.state.expandedFolders, label];
    this.commit();
  }

  /* directory-tree fold + selection (DIR6): clicking selects and toggles the
     fold — but not while a query holds every surviving folder open (N31/N32) */
  toggleDir(id: string): void {
    this.state.selectedDirId = id === "" ? null : id;
    if (!this.state.pickerSearch.trim())
      this.state.expandedDirs = this.state.expandedDirs.includes(id)
        ? this.state.expandedDirs.filter((x) => x !== id)
        : [...this.state.expandedDirs, id];
    this.commit();
  }

  openNewFolder(): void {
    this.state.newFolderOpen = true;
    this.state.newFolderError = null;
    this.commit();
  }

  cancelNewFolder(): void {
    this.state.newFolderOpen = false;
    this.state.newFolderError = null;
    this.commit();
  }

  async createFolder(section: DirectorySection, name: string): Promise<void> {
    const input = { section, name, parentId: this.state.selectedDirId };
    const reason = validateDirectoryInput(input, this.state.directories);
    if (reason) {
      this.state.newFolderError = reason;
      return this.commit();
    }
    try {
      const res = await fetch("/api/directories", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        this.state.newFolderError = await failureReason(
          res,
          "Couldn't create the folder — is the server healthy?",
        );
        return this.commit();
      }
      const dir = (await res.json()) as Directory;
      this.state.directories = [...this.state.directories, dir];
      this.state.newFolderOpen = false;
      this.state.newFolderError = null;
      if (dir.parentId && !this.state.expandedDirs.includes(dir.parentId))
        this.state.expandedDirs = [...this.state.expandedDirs, dir.parentId];
      this.state.selectedDirId = dir.id;
    } catch {
      this.state.newFolderError = "Couldn't create the folder — is the server healthy?";
    }
    this.commit();
  }

  /* ---- form navigation (the form VIEWS themselves belong to surface agents:
     ProjectFormView, ItemFormView, MappingFormView, AllocationFormView) ---- */

  openForm(editingId: string | null): void {
    this.state.editingId = editingId;
    this.state.formError = null;
    this.state.view = "project-form";
    this.state.navigated = true;
    this.commit();
  }

  /* ---- record-state transitions and deletes, ported verbatim ---- */

  async setRecordState(p: Project, recordState: RecordState, verb: string): Promise<void> {
    this.state.listError = null;
    const oops = `Couldn't ${verb} ${p.name} — is the server healthy?`;
    try {
      const res = await fetch(`/api/projects/${p.id}/record-state`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ recordState }),
      });
      if (!res.ok) {
        this.state.listError = await failureReason(res, oops);
      } else {
        const saved = (await res.json()) as Project;
        this.state.projects = this.state.projects.map((x) => (x.id === saved.id ? saved : x));
      }
    } catch {
      this.state.listError = oops;
    }
    this.commit();
  }

  async deleteProject(p: Project): Promise<void> {
    if (
      !window.confirm(`Permanently delete "${p.name}" and its whole history? This cannot be undone.`)
    )
      return;
    this.state.listError = null;
    const oops = `Couldn't delete ${p.name} — is the server healthy?`;
    try {
      const res = await fetch(`/api/projects/${p.id}`, { method: "DELETE" });
      if (!res.ok) this.state.listError = await failureReason(res, oops);
      else {
        this.state.projects = this.state.projects.filter((x) => x.id !== p.id);
        this.state.openProjectId = null;
        this.state.area = null;
      }
    } catch {
      this.state.listError = oops;
    }
    this.commit();
  }

  /* ---- on-navigation data reads, ported verbatim ---- */

  async openRoles(): Promise<void> {
    try {
      this.state.roles = await loadJson<Role[]>("/api/roles");
      this.state.loadError = false;
    } catch {
      this.state.catalogAuthority = false;
      this.state.loadError = true;
      return this.commit();
    }
    /* CR8 after the MVP cut: the allocation-derived branch is gone, so
       catalog authority is the granted Admin role alone — the same rule the
       server enforces. */
    this.state.catalogAuthority = this.state.me.roles.includes("admin");
    this.commit();
  }

  /* The PDP catalog read (pdp-catalog.md PC16): the three lists and the
     Roles catalog in one group, one fault domain — role ids render as names,
     so the roles ride along. `reloadPdp` re-reads after a write without the
     pending flag, so an open form is not unmounted by a Loading flash. */
  async openPdp(): Promise<void> {
    this.state.pdpPending = true;
    this.state.pdpError = false;
    this.commit();
    await this.reloadPdp();
    this.state.pdpPending = false;
    this.commit();
  }

  async reloadPdp(): Promise<void> {
    try {
      const [deliverables, criteria, gates, roles] = await Promise.all([
        loadJson<PdpDeliverable[]>("/api/pdp/deliverables"),
        loadJson<PdpCriterion[]>("/api/pdp/criteria"),
        loadJson<PdpGate[]>("/api/pdp/gates"),
        loadJson<Role[]>("/api/roles"),
      ]);
      Object.assign(this.state, { pdp: { deliverables, criteria, gates }, roles, pdpError: false });
    } catch {
      this.state.pdpError = true;
    }
    /* the same rule the server enforces (CR8 after the MVP cut) */
    this.state.catalogAuthority = this.state.me.roles.includes("admin");
    this.commit();
  }

  async openPermissions(): Promise<void> {
    this.state.usersPending = true;
    this.state.usersError = false;
    this.commit();
    try {
      this.state.users = await loadJson<UserWithGrants[]>("/api/users");
      this.state.organizerRoles = await loadJson<OrganizerRole[]>("/api/organizer-roles");
      this.state.usersError = false;
    } catch {
      this.state.usersError = true;
    }
    this.settled("usersPending");
  }
}

/* ---- React binding (was store.tsx, folded in so `./store` resolves to a
   single module under both tsc and esbuild). A context carries the one Store
   instance; useAppState() subscribes to its snapshots through
   useSyncExternalStore, so any component reading state repaints when an action
   commits. This block uses createElement rather than JSX so store.ts stays a
   valid .ts file. ---- */

const StoreContext = createContext<Store | null>(null);

export function AppProvider({ children }: { children: ReactNode }): JSX.Element {
  /* one Store per mount — a ref so React never reconstructs it on re-render */
  const ref = useRef<Store | null>(null);
  ref.current ??= new Store();
  return createElement(StoreContext.Provider, { value: ref.current }, children);
}

/* The raw store — actions live here (store.selectNav(...), store.openProject(...)).
   Reading `store.state` outside a render is fine; inside a render prefer the
   `state` returned by useAppState so the component subscribes to changes. */
export function useStore(): Store {
  const store = useContext(StoreContext);
  if (!store) throw new Error("useStore must be used within <AppProvider>");
  return store;
}

/* state + the store together. `state` is the live snapshot (subscribed);
   `store` carries every action. This is the ONE hook a view component uses. */
export function useAppState(): { state: ClientState; store: Store } {
  const store = useStore();
  const state = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  return { state, store };
}
